import type { Location, NavigateFunction } from 'react-router-dom'

/**
 * Handles navigation to Risk Management "QRA" views
 * (i.e. current state, saved state, etc..)
 *
 * This is put into common utils for the moment but may make sense
 * to put into Risk Management feature directory once there are
 * enough of related Risk Management utils.
 */

export interface IQRANavUtilsConsumerComponentInitProps {
  /**
   * Path to `../qra/`, '../states/123/', '../compare/1/2/'
   * within RiskManagment.
   */
  qraBasePath: string
  /**
   * Path to system or particular line view, similar to examples
   * listed above but with an optional `QRAViewType` appended.
   */
  qraViewBasePath: string
}

export enum QRAViewType {
  Graph = 'graphs',
  Table = 'data-table'
}

/**
 * Extract line id from line view's (assumed) qraViewBasePath
 * @param qraViewBasePath
 */
export const getLineIdFromQRAViewBasePath = (
  qraViewBasePath: string
): string | undefined => {
  // Assume line id is always specified in this format: `/line/#`
  const match = qraViewBasePath.match(/\/line\/(?<lineId>\w+)\/?/)

  return match?.groups?.lineId
}

// @region QRA System Graphs View Redirect
const qraSavedStateRegex =
  /risk-management\/assessments\/states\/(\d+)(?:\/assessments\/(\d+))?/

const qraCompareStatesRegex =
  /risk-management\/assessments\/compare\/(\d+)\/(\d+)/

/**
 * Helper method to redirect back to system view - graphs/map tab
 * if user is on any of the QRA pages (current/saved/compare state)
 *
 * NOTE: very fragile hard coded paths here, will need to revist if
 * we ever make any routing changes under /risk-management/ path.
 * This may update path with original query param, but it is okay
 * as long as the search param itself does not change at all which
 * avoids related effects on search param change.
 */
export const qraSystemGraphsViewRedirectHelper = (
  location: Location,
  navigate: NavigateFunction,
  keepAssessment = true
): void => {
  const { pathname, search } = location

  if (!pathname.includes('/risk-management')) {
    return
  }

  const parentPath = pathname.slice(0, pathname.indexOf('/risk-management'))

  if (pathname.includes('risk-management/assessments/state')) {
    const matches = pathname.match(qraSavedStateRegex)

    if (matches != null) {
      const [, stateId, assessmentId] = matches

      if (keepAssessment && assessmentId != null) {
        navigate(
          `${parentPath}/risk-management/assessments/states/${stateId}/assessments/${assessmentId}${search}`
        )
      } else {
        navigate(
          `${parentPath}/risk-management/assessments/states/${stateId}${search}`
        )
      }
    }
  } else if (pathname.includes('risk-management/assessments/compare')) {
    const matches = pathname.match(qraCompareStatesRegex)

    if (matches != null) {
      const [, baseStateId, compareStateId] = matches

      navigate(
        `${parentPath}/risk-management/assessments/compare/${baseStateId}/${compareStateId}${search}`
      )
    }
  } else if (!pathname.includes('risk-management/assessments')) {
    // Current state view
    // NOTE: Assumes that we do not have any other paths under risk management
    navigate(`${parentPath}/risk-management/qra${search}`)
  }
}
// @endregion
